import { useEffect, useMemo, useRef, useState } from "react";
import { SettingsIcon } from "lucide-react";
import { useNavigate } from "react-router";

import { Column, TopbarContainer, TopbarTitle } from "@/components";
import { NotificationsTable } from "@/components/notifications/NotificationsTable";
import { SelectAndFilterBar } from "@/components/notifications/SelectAndFilterBar";
import { Button } from "@/components/ui/button";

import { useSetTitle } from "@/hooks/useSetTitle";
import { useGetNotifications } from "@/hooks/queries/useGetNotifications";
import { useUpdateSetNotifications } from "@/hooks/updateSets/useUpdateSetNotifications";
import { defaultNotificationFilterState } from "@/lib/constants";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_NOTIFICATION_SURFACES, MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { filterNotifications } from "@/lib/notificationFilters";
import { NotificationFilterState } from "@/types";

export const NotificationsPage = () => {
  const navigate = useNavigate();

  /* Database CRUD Operations */
  // Read
  const {
    data: notifications = [],
    isPending,
    isError,
  } = useGetNotifications();
  const { mutate: updateNotifications } = useUpdateSetNotifications();

  /* React Hooks */
  const [filterState, setFilterState] = useState<NotificationFilterState>(
    defaultNotificationFilterState,
  );
  const [selectedIds, setSelectedIds] = useState<Set<number>>(() => new Set());
  const hasDismissedOnVisitRef = useRef(false);

  const filteredNotifications = useMemo(
    () => filterNotifications(notifications, filterState),
    [notifications, filterState],
  );

  useSetTitle({
    title: "Notifications",
  });

  useEffect(() => {
    mixpanelUtils.trackScreenView(MIXPANEL_SCREENS.NOTIFICATIONS);
  }, []);

  // On visit of notification page, mark unreads as dismissed to avoid later popup
  // Unless they have a pending remindAt, then don't mark as dismiss so it pos up later as planned
  useEffect(() => {
    if (isPending || hasDismissedOnVisitRef.current) return;
    hasDismissedOnVisitRef.current = true;

    const undismissedIds = notifications
      .filter(
        (notification) =>
          notification.readAt === null && notification.dismissedAt === null,
      )
      .map((notification) => notification.id);

    if (undismissedIds.length > 0) {
      updateNotifications({ notificationIds: undismissedIds, dismiss: true });
    }
  }, [isPending, notifications, updateNotifications]);

  /* Handlers */
  const handleSettingsClick = () => {
    mixpanelUtils.trackButtonClick(
      "Notification settings",
      MIXPANEL_SCREENS.NOTIFICATIONS,
    );
    void navigate("/notifications/settings");
  };

  return (
    <Column className="h-svh">
      <TopbarContainer>
        <TopbarTitle>Notifications</TopbarTitle>
        <Button
          className="rounded-full"
          onClick={handleSettingsClick}
          variant="outline"
        >
          <SettingsIcon className="h-4 w-4" />
          Settings
        </Button>
      </TopbarContainer>
      <Column className="min-h-0 flex-1 overflow-y-auto px-12 pb-16 pt-8">
        <Column className="mx-auto w-full max-w-3xl gap-6">
          <SelectAndFilterBar
            allNotifications={notifications}
            filterState={filterState}
            filteredNotifications={filteredNotifications}
            selectedIds={selectedIds}
            setFilterState={setFilterState}
            setSelectedIds={setSelectedIds}
          />
          <NotificationsTable
            hasAnyNotifications={notifications.length > 0}
            isError={isError}
            isPending={isPending}
            notifications={filteredNotifications}
            selectedIds={selectedIds}
            setSelectedIds={setSelectedIds}
            trackingSurface={MIXPANEL_NOTIFICATION_SURFACES.PAGE}
          />
        </Column>
      </Column>
    </Column>
  );
};
